import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
} from '@mui/material';
import { translateKey } from 'src/locales/translate';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { templateApi } from 'src/api/template';
import Editor from 'src/components/editor';
import Markdown from 'src/components/markdown/Markdown';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { GetVisitByIdStatusEnum } from 'woofwoof-api';
import VisitModalEmptyState from './VisitModalEmptyState';

type OrganizationVisitModalNotesTabsProps = {
  visit?: any;
  activeTab?: number;
  isEditing: boolean;
  notes: string;
  internalNotes: string;
  onNotesChange: (value: string) => void;
  onInternalNotesChange: (value: string) => void;
};

// Notes are the clinical record, not supporting detail, so they keep the
// default body colour; only the size is normalised here.
export const StyledMarkdown = styled(Markdown)(({ theme }) => ({
  '& *': {
    fontSize: `${theme.typography.body1.fontSize} !important`,
  },
}));

export default function OrganizationVisitModalNotesTabs({
  visit,
  activeTab,
  isEditing,
  notes,
  internalNotes,
  onNotesChange,
  onInternalNotesChange,
}: OrganizationVisitModalNotesTabsProps) {
  const isVisitCompleted = visit.status === GetVisitByIdStatusEnum.Completed;
  const value = activeTab ?? (isVisitCompleted ? 0 : 1);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const { isProfessionalRoleSelected, isOrganizationAdminRoleSelected } =
    useOrganizationContext();

  const { data: templatesData, isLoading: isTemplatesDataLoading } = useQuery(
    ['templates'],
    () => templateApi.templateControllerGetTemplates(),
    { enabled: isProfessionalRoleSelected || isOrganizationAdminRoleSelected },
  );

  const handleTemplateChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const templateId = event.target.value;
      setSelectedTemplate(templateId);

      if (!templateId) return;

      const template = templatesData?.data.find(
        (template) => template.id === templateId,
      );
      // Apply template based on which tab is active
      if (value === 0) {
        onNotesChange(template?.content || '');
      } else if (value === 1) {
        onInternalNotesChange(template?.content || '');
      }
    },
    [templatesData?.data, value, onNotesChange, onInternalNotesChange],
  );

  return isProfessionalRoleSelected || isOrganizationAdminRoleSelected ? (
    <Stack
      sx={{ gap: 2, flex: 1, mt: !isVisitCompleted ? 2 : 0, minHeight: 100 }}
    >
      <Stack sx={{ gap: 1, flex: 1 }}>
        {isEditing && isProfessionalRoleSelected && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="template-select-label">{translateKey("visit.selectTemplate")}</InputLabel>
              <Select
                labelId="template-select-label"
                id="template-select"
                value={selectedTemplate}
                onChange={handleTemplateChange}
                label={translateKey("visit.selectTemplate")}
                disabled={isTemplatesDataLoading}
                startAdornment={
                  isTemplatesDataLoading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null
                }
              >
                <MenuItem value="">
                  <em>{translateKey("visit.templateNotSelected")}</em>
                </MenuItem>
                {templatesData?.data.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {value === 0 &&
          (isEditing ? (
            <Editor
              simple
              id="edit-notes"
              enableDictation
              value={notes}
              onChange={onNotesChange}
              sx={{ height: '100%' }}
            />
          ) : visit.notes ? (
            <StyledMarkdown>{visit.notes}</StyledMarkdown>
          ) : (
            <VisitModalEmptyState message="Brak notatek" />
          ))}
        {value === 1 &&
          (isEditing ? (
            <Editor
              simple
              id="edit-internal-notes"
              enableDictation
              value={internalNotes}
              onChange={onInternalNotesChange}
              sx={{ height: '100%' }}
            />
          ) : visit.internalNotes ? (
            <StyledMarkdown>{visit.internalNotes}</StyledMarkdown>
          ) : (
            <VisitModalEmptyState message="Brak notatek" />
          ))}
      </Stack>
    </Stack>
  ) : null;
}
